import { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { useConfirm } from 'material-ui-confirm';

import {
  Cancel as CancelIcon,
  ContentCopyOutlined as ContentCopyOutlinedIcon,
  DeleteOutlineOutlined as DeleteOutlineOutlinedIcon,
  EditOutlined as EditOutlinedIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';

import { Button, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';

import {
  deleteTextActionAlert,
  fetchTextActionAlert,
  stopTextActionAlert,
} from 'store/actions/campaigns';
import { campaignsSelectors } from 'store/selectors/campaigns';
import { setSnackbarError, setSnackbarSuccess, setSnackbarWarning } from 'store/actions/global';

import { cancellationStatuses, statuses } from 'utils/constants';
import { useQuery } from 'utils/helpers';

import TopCard from 'common/containers/TopCard';
import PhoneView from 'common/components/PhoneView';

const { REACT_APP_LANDING_PAGE_URL } = process.env;

const chipColor = (value, sendAtUtc) => {
  const { draft, published } = statuses;

  switch (value) {
    case published: {
      const sent = new Date().getTime() - new Date(sendAtUtc).getTime() > 0;

      return sent ? 'secondary' : 'blue';
    }
    case draft:
      return 'grey';
    default:
      return null;
  }
};

const chipLabel = (value, sendAtUtc) => {
  const { draft, published } = statuses;

  switch (value) {
    case published: {
      const sent = new Date().getTime() - new Date(sendAtUtc).getTime() > 0;

      return sent ? 'Sent' : 'Scheduled';
    }
    case draft:
      return 'Draft';
    default:
      return null;
  }
};

const TextActionAlertTopCard = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const confirm = useConfirm();
  const { draft } = statuses;
  const { probablyCancelled } = cancellationStatuses;
  const { actionAlertUuid, actionCenterUuid, campaignUuid, organizationUuid } = useParams();
  const backUrl = useQuery().get('backUrl');

  const { actionAlertDetails } = useSelector(state => ({
    actionAlertDetails: campaignsSelectors.getActionAlertDetails(state),
  }));

  const { contactLists, isCancellable, message, name, sendAt, sendAtUtc, status, subject } =
    actionAlertDetails;

  const [isMessageOpen, setMessageOpen] = useState(false);

  const handleMessageClose = () => setMessageOpen(false);

  const handleDeleteActionAlert = () => {
    confirm({
      confirmationText: 'Delete',
      description: 'Deleting this Action Alert will also remove any related recipient analytics.',
      title: 'Delete the Action Alert?',
    })
      .then(() => dispatch(deleteTextActionAlert(actionAlertUuid)))
      .then(() =>
        history.push(
          `/${organizationUuid}/campaigns/${campaignUuid}/action-centers/${actionCenterUuid}?tab=0`
        )
      );
  };

  const handleNavigateToWizard = actionType => {
    history.push(
      `/${organizationUuid}/${campaignUuid}/${actionCenterUuid}/create-text-action-alert/${actionType}/${actionAlertUuid}`
    );
  };

  const handleStopActionAlert = () => {
    dispatch(stopTextActionAlert(actionAlertUuid))
      .then(({ message: stopMessage, status: stoppedActionAlertStatus }) => {
        if (stoppedActionAlertStatus === probablyCancelled) {
          dispatch(setSnackbarWarning({ message: stopMessage }));
        } else {
          dispatch(setSnackbarSuccess({ message: stopMessage }));
        }
        dispatch(fetchTextActionAlert(actionAlertUuid));
      })
      .catch(({ message: stopMessage }) => dispatch(setSnackbarError({ message: stopMessage })));
  };

  const handleConfirmStopActionAlert = () => {
    confirm({
      cancellationText: 'No',
      confirmationText: 'Yes, Cancel',
      description: '',
      title: 'Cancel send the Action Alert?',
    }).then(() => handleStopActionAlert(actionAlertUuid));
  };

  const topCardData = useCallback(() => {
    const chipData = {
      chipColor: chipColor(status, sendAtUtc),
      chipLabel: chipLabel(status, sendAtUtc),
      chipOptions: [],
      chipOptionsHeader: 'Change status',
    };

    const actionButtons = [
      {
        color: 'grey',
        disabled: status !== draft,
        name: 'Edit',
        onClick: () => handleNavigateToWizard('edit'),
        startIcon: <EditOutlinedIcon />,
      },
      {
        color: 'grey',
        name: 'Duplicate',
        onClick: () => handleNavigateToWizard('duplicate'),
        startIcon: <ContentCopyOutlinedIcon />,
      },
      {
        color: 'grey',
        name: 'Delete',
        onClick: handleDeleteActionAlert,
        startIcon: <DeleteOutlineOutlinedIcon />,
      },
    ];

    if (isCancellable) {
      actionButtons.unshift({
        color: 'grey',
        disabled: false,
        name: 'Cancel send',
        onClick: handleConfirmStopActionAlert,
        startIcon: <CancelIcon />,
      });
    }

    const additionalActionButtons = [
      {
        color: 'primary',
        endIcon: <OpenInNewIcon />,
        name: 'Preview message',
        onClick: () => setMessageOpen(true),
      },
    ];

    const details = [
      ...(sendAt
        ? [
            {
              label: 'Scheduled at',
              value: new Date(sendAt).toLocaleString('us-US'),
            },
          ]
        : []),
      {
        label: 'Subject',
        value: subject,
      },
      {
        label: 'Lists',
        value: contactLists?.map(({ name: listName }) => listName).join(', '),
      },
    ];

    const backButtonLink =
      backUrl ||
      `/${organizationUuid}/campaigns/${campaignUuid}/action-centers/${actionCenterUuid}?tab=0`;

    return {
      actionButtons,
      additionalActionButtons,
      backButtonLink,
      chipData,
      details,
      entityTypes: ['Action Alert', 'Text Message'],
      title: name || '',
    };
  }, [name, status, draft, isCancellable, probablyCancelled, organizationUuid]);

  return (
    <>
      <TopCard {...topCardData()} />
      <Dialog
        aria-labelledby="max-width-dialog-message"
        maxWidth="lg"
        open={isMessageOpen}
        onClose={handleMessageClose}
      >
        <DialogTitle id="max-width-dialog-message">{subject}</DialogTitle>
        <DialogContent>
          <PhoneView link={`${REACT_APP_LANDING_PAGE_URL}/${actionCenterUuid}`} text={message} />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleMessageClose}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default TextActionAlertTopCard;
